import React, { useState } from "react";
import { getOnlineUsers } from "../../api/MeetingApi";
import ContainerCard from "../../common/CardContainer";
import { downloadCsv, toCsv } from "../../common/csv";
import { OnlineUser } from "../../data/OnlineUser";
import { UserHouses } from "../../data/User";

/** "Lay 12 · Clergy 4 · Bishops 1 · No house 2" (zero groups omitted) */
export function houseSummary(users: OnlineUser[]): string {
  const count = (house: string) => users.filter((u) => u.house === house).length;
  const noHouse = users.filter((u) => !u.house).length;
  return [
    [UserHouses.Lay, count(UserHouses.Lay)],
    [UserHouses.Clergy, count(UserHouses.Clergy)],
    [UserHouses.Bishops, count(UserHouses.Bishops)],
    ["No house", noHouse],
  ]
    .filter(([, n]) => n > 0)
    .map(([label, n]) => `${label} ${n}`)
    .join(" · ");
}

/** The loaded list as CSV: Name, Email, House, In person, Roles. */
export function onlineUsersCsv(users: OnlineUser[]): string {
  return toCsv([
    ["Name", "Email", "House", "In person", "Roles"],
    ...users.map((u) => [u.name, u.userName, u.house, u.isInPerson ? "Yes" : "No", u.userRoles]),
  ]);
}

/** e.g. online-users-2026-10-10-1342.csv (local time the list was loaded) */
export function onlineUsersFileName(loadedAt: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const d = loadedAt;
  return `online-users-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.csv`;
}

/**
 * On-demand list of who is connected right now, for meeting control. Nothing is
 * loaded until the organiser asks; Refresh reloads and Clear hides the list again.
 */
function OnlineUsersList() {
  const [users, setUsers] = useState<OnlineUser[] | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      setUsers(await getOnlineUsers());
      setLoadedAt(new Date());
    } catch (e: any) {
      setError(`Could not load online users: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  function clear() {
    setUsers(null);
    setLoadedAt(null);
    setError("");
  }

  return (
    <ContainerCard heading="Online Users">
      {users === null ? (
        <button className="btn btn-secondary btn-sm" disabled={loading} onClick={load}>
          {loading ? "Loading..." : "List online users"}
        </button>
      ) : (
        <>
          <div className="d-flex align-items-center gap-2 mb-2">
            <strong>
              <span className="sl-num">{users.length}</span> online
            </strong>
            <span className="ms-auto">
              <button className="btn btn-light btn-sm" disabled={loading} onClick={load}>
                {loading ? "Refreshing..." : "Refresh"}
              </button>
              <button
                className="btn btn-light btn-sm ms-1"
                disabled={loading || users.length === 0}
                onClick={() => downloadCsv(onlineUsersFileName(loadedAt ?? new Date()), onlineUsersCsv(users))}
                title="Download this list (as loaded) as a CSV file"
              >
                Download
              </button>
              <button className="btn btn-light btn-sm ms-1" disabled={loading} onClick={clear}>
                Clear
              </button>
            </span>
          </div>
          {users.length > 0 ? <div className="small text-muted mb-1">{houseSummary(users)}</div> : null}
          {users.length === 0 ? (
            <p className="text-muted mb-0">Nobody is connected right now.</p>
          ) : (
            <div className="sl-online-users">
              <table className="table table-sm mb-0">
                <tbody>
                  {users.map((user) => (
                    <tr key={user.userId}>
                      <td>
                        {user.name || user.userName}
                        {user.isInPerson ? <span className="text-muted small"> · in person</span> : null}
                      </td>
                      <td className="text-end text-nowrap">
                        {user.house ? <span className="sl-chip sl-chip-type">{user.house}</span> : <span className="text-muted small">no house</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="form-text mb-0">
            Connected in the last few minutes{loadedAt ? `, as at ${loadedAt.toLocaleTimeString()}` : ""}. Users with no house are not counted in the house totals.
          </div>
        </>
      )}
      {error ? <div className="alert alert-danger py-2 mt-2 mb-0">{error}</div> : null}
    </ContainerCard>
  );
}

export default OnlineUsersList;
